/* Decoding HEIC in the browser, because nothing else will.
 *
 * Every current iPhone records HEIC by default and the recipient is usually
 * on Windows, where the file will not open. That is the entire reason
 * /heic-to-jpg exists -- and it is the one conversion on this site with no
 * ordinary route:
 *
 *   - THE BROWSER cannot decode it. Chrome and Firefox refuse on every
 *     platform; only Safari manages it. So the canvas trick that powers
 *     /svg-to-png has nothing to draw.
 *   - IMAGEENGINE does not convert it either. Measured 2026-09-10 on a real
 *     HEVC-coded HEIC: pass_true, f_jpeg, f_webp, f_avif and a bare w_ all
 *     return the source with `imgeng-img-status: origin-object`, while an
 *     AVIF of the same ISO-BMFF family and an ordinary JPEG both optimise.
 *     A reproducible report is with the ImageEngine team.
 *
 * So the decoder ships with the page. libheif compiled to WebAssembly, kept
 * verbatim in public/vendor/libheif/ -- see the README there for the LGPL
 * position and why it must not be bundled.
 *
 * TWO LOSSY STEPS, DELIBERATELY. The HEIC is decoded here and re-encoded as
 * JPEG before it is uploaded; anything else the visitor asks for is then made
 * by ImageEngine from that JPEG. Generation loss, accepted knowingly and
 * temporarily: it is the difference between a file that opens and a file that
 * does not. When the engine supports HEIC natively this module and the vendor
 * directory both go away, and the page becomes an ordinary CDN conversion.
 */
import { MAX_EDGE, MAX_AREA } from "./imageTransform"

/* Where the vendored library lives. Not bundled and not versioned into the
 * filename: it is copied through the build untouched so that it stays
 * replaceable, which is what the licence asks for. */
export const LIBHEIF_DIR = "/vendor/libheif/"

/* The quality the JPEG is written at.
 *
 * High, and on purpose. This file is about to be handed to ImageEngine, which
 * will re-encode it at whatever the visitor actually asked for -- so this
 * encode is an intermediate, and every artefact it introduces is one the CDN
 * then has to carry. The cost of being generous here is upload time; the cost
 * of being mean is permanent. */
export const INTERMEDIATE_QUALITY = 0.92

export function looksLikeHeic(file) {
    if (!file) return false
    const type = (file.type || "").toLowerCase()
    if (type === "image/heic" || type === "image/heif" ||
        type === "image/heic-sequence" || type === "image/heif-sequence") return true
    /* BY NAME AS WELL, because `file.type` is the operating system's
     * extension map and is absent often enough to matter -- the same reason
     * namedByAttr() exists in lib/task.js. A .heic off Windows frequently
     * arrives with no type at all. */
    return /\.hei[cf]$/i.test(file.name || "")
}

/* The name the JPEG is saved under. Extension REPLACED, not appended, for the
 * reason gifNameFor() and pngNameFor() both record. */
export function jpegNameFor(name) {
    const base = String(name || "photo").replace(/\.hei[cf]$/i, "")
    return (base || "photo") + ".jpg"
}

/* How big to draw it. The iOS guard again: above roughly 16.7 megapixels
 * Safari abandons a canvas and hands back a BLANK one rather than an error,
 * and a phone photograph is well within range of that -- a 48 MP sensor is
 * three times over. Shape is preserved; a vector has no correct size but a
 * photograph certainly does. */
export function fitWithin(w, h, maxEdge = MAX_EDGE, maxArea = MAX_AREA) {
    let W = Math.max(1, w), H = Math.max(1, h)
    const longest = Math.max(W, H)
    if (longest > maxEdge) { const k = maxEdge / longest; W *= k; H *= k }
    if (W * H > maxArea) { const k = Math.sqrt(maxArea / (W * H)); W *= k; H *= k }
    // floored rather than rounded, so a cap cannot be rounded back over --
    // the arithmetic slip lib/svgRaster.js records paying for.
    return {width: Math.max(1, Math.floor(W)), height: Math.max(1, Math.floor(H))}
}

/* THE LIBRARY IS FETCHED ONCE, AND ONLY IF SOMEBODY CONVERTS SOMETHING.
 *
 * 1.4 MB of WebAssembly plus a 91 KB loader. workbox-config.js excludes it
 * from the precache for exactly that reason, so this is the only thing that
 * ever asks for it. Memoised on the promise rather than the result, so two
 * files dropped together do not start two downloads. */
let pending = null
export function loadLibheif(dir = LIBHEIF_DIR) {
    if (pending) return pending
    pending = new Promise((resolve, reject) => {
        if (window.libheif) return resolve(window.libheif)
        const el = document.createElement("script")
        el.src = dir + "libheif.js"
        el.async = true
        el.onload = () => window.libheif
            ? resolve(window.libheif)
            : reject(new Error("loader-empty"))
        el.onerror = () => reject(new Error("loader-missing"))
        document.head.appendChild(el)
    }).then(async (factory) => {
        /* THE WASM IS FETCHED HERE AND HANDED OVER, not left to the module.
         *
         * `locateFile` is accepted and then ignored: this build tries a
         * SYNCHRONOUS fetch of the binary and throws a bare string --
         * "sync fetching of the wasm failed: you can preload it to
         * Module['wasmBinary'] manually" -- which is not an Error, has no
         * message and no stack, and surfaces as `undefined` anywhere that
         * reads err.message. The package's own wasm.js does the same thing
         * with readFileSync, so this is the intended shape rather than a
         * workaround.
         *
         * It also puts the 1.4 MB transfer somewhere we can see it: an
         * ordinary fetch, cached at runtime by the service worker like any
         * other asset. */
        const res = await fetch(dir + "libheif.wasm")
        if (!res.ok) throw new Error("wasm-missing")
        const wasmBinary = await res.arrayBuffer()
        const mod = await factory({wasmBinary})

        /* AND THEN WAIT FOR `ready`, which is a separate thing again.
         *
         * The factory resolving means the module OBJECT exists; the runtime
         * behind it may not be up yet, and decode() on a module that is not
         * ready returns an EMPTY ARRAY rather than throwing. That surfaces
         * three lines later as "Cannot read properties of undefined (reading
         * 'get_width')", which points at the wrong place entirely.
         *
         * heic-decode, the thin wrapper over this same library, awaits it for
         * the same reason and its comment says the quiet part: "currently it
         * is synchronous but it might be async in the future". */
        if (mod && mod.ready) await mod.ready
        return mod
    })
    return pending
}

/* HEIC in, JPEG out, entirely on the device.
 *
 * Everything above is pure and driven by tests/heic_decode_test.mjs in node;
 * this needs a document and a decoder, and is covered by the browser suite.
 */
export async function heicToJpeg(file, options) {
    const {
        quality = INTERMEDIATE_QUALITY,
        maxEdge = MAX_EDGE,
        maxArea = MAX_AREA,
        onProgress = null,
    } = options || {}

    if (onProgress) onProgress("loading")
    let lib
    try {
        lib = await loadLibheif()
    } catch (e) {
        throw new Error("decoder-unavailable")
    }

    if (onProgress) onProgress("decoding")
    const bytes = new Uint8Array(await file.arrayBuffer())

    let images
    try {
        images = new lib.HeifDecoder().decode(bytes)
    } catch (e) {
        throw new Error("unreadable")
    }
    if (!images || !images.length) throw new Error("unreadable")

    /* THE FIRST IMAGE, and saying so matters. A Live Photo and a burst are
     * both HEIC files carrying several images; converting the first is what
     * every other tool does and what somebody expects from a photograph, but
     * it is a choice rather than a fact. */
    const image = images[0]
    const w = image.get_width(), h = image.get_height()
    if (!(w > 0) || !(h > 0)) throw new Error("unreadable")

    // decoded at full size, then drawn down -- the decoder has no scaler
    const full = document.createElement("canvas")
    full.width = w; full.height = h
    const fctx = full.getContext("2d")
    const buffer = fctx.createImageData(w, h)

    await new Promise((resolve, reject) => {
        image.display(buffer, (ok) => ok ? resolve() : reject(new Error("unreadable")))
    })
    fctx.putImageData(buffer, 0, 0)

    if (onProgress) onProgress("encoding")
    const size = fitWithin(w, h, maxEdge, maxArea)
    let surface = full
    if (size.width !== w || size.height !== h) {
        surface = document.createElement("canvas")
        surface.width = size.width; surface.height = size.height
        surface.getContext("2d").drawImage(full, 0, 0, size.width, size.height)
    }

    const blob = await new Promise((resolve, reject) => {
        try {
            surface.toBlob((b) => b ? resolve(b) : reject(new Error("encode-failed")),
                           "image/jpeg", quality)
        } catch (e) { reject(new Error("encode-failed")) }
    })

    return {
        file: new File([blob], jpegNameFor(file.name), {type: "image/jpeg"}),
        width: size.width,
        height: size.height,
        natural: {width: w, height: h},
        images: images.length,
    }
}
